import { useMemo } from 'react';

import { cn } from '@/lib/cn';
import { PlanBacklog } from '@/features/calendars/PlanBacklog';
import type { BacklogSort } from '@/features/calendars/backlogSort';
import type { Client, Project, Task } from '@/features/tasks/types';

interface Props {
  tasks: Task[];
  clients: Client[];
  projects: Project[];
  timezone: string;
  sort: BacklogSort;
  onSortChange: (sort: BacklogSort) => void;
  clientColor: (clientId: number) => string;
  selectedTaskId: number | null;
  onSelectTask: (task: Task) => void;
  onSchedule: (task: Task) => void;
  dropActive?: boolean;
  loading?: boolean;
  loadError?: boolean;
  onRetry?: () => void;
}

/**
 * Compact backlog for the Daily page, drag chips onto today's calendar.
 */
export function DailyBacklog({
  tasks,
  clients,
  projects,
  timezone,
  sort,
  onSortChange,
  clientColor,
  selectedTaskId,
  onSelectTask,
  onSchedule,
  dropActive = false,
  loading = false,
  loadError = false,
  onRetry,
}: Props) {
  const schedulable = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.status !== 'done' &&
          t.status !== 'dropped' &&
          t.status !== 'scheduled',
      ),
    [tasks],
  );

  return (
    <section
      data-daily-backlog-drop
      className={cn(
        'rounded-lg border border-border bg-surface-1 transition-colors',
        dropActive &&
          'border-[var(--accent-border)] bg-[var(--accent-quiet)] ring-2 ring-[var(--ring)]',
      )}
    >
      <header className="border-b border-border px-4 py-3">
        <h2 className="text-body font-semibold">
          {dropActive ? 'Drop to return to backlog' : 'Backlog'}
        </h2>
        <p className="text-small text-text-secondary">
          {loading
            ? 'Loading…'
            : loadError
              ? 'Could not load'
              : `${schedulable.length} task${schedulable.length === 1 ? '' : 's'}: drag onto the calendar`}
        </p>
      </header>

      <div className="max-h-[min(36dvh,18rem)] overflow-y-auto p-3">
        {loading ? (
          <div className="space-y-2" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-surface-2" />
            ))}
          </div>
        ) : loadError ? (
          <p role="alert" className="text-body text-danger">
            Could not load backlog.{' '}
            {onRetry && (
              <button type="button" className="underline" onClick={onRetry}>
                Retry
              </button>
            )}
          </p>
        ) : (
          <PlanBacklog
            tasks={schedulable}
            clients={clients}
            projects={projects}
            timezone={timezone}
            sort={sort}
            onSortChange={onSortChange}
            clientColor={clientColor}
            selectedTaskId={selectedTaskId}
            onSelectTask={onSelectTask}
            onSchedule={onSchedule}
          />
        )}
      </div>
    </section>
  );
}
